import { BASIC_CONSTANTS_NETWORK } from "@/app/network/models/NetworkConstants.ts";
import { NetworkError } from "@/app/network/models/ApiResponse.ts";

export { NetworkError };

class NetworkUtil {
    private getAuthHeaders(): Record<string, string> {
        const headers: Record<string, string> = {};

        // const initData = getInitData(); // временно отключено
        // if (initData) {
        //     headers['X-Telegram-Init-Data'] = initData;
        // }

        try {
            const stored = localStorage.getItem('auth_tokens');
            if (stored) {
                const token = JSON.parse(stored).accessToken;
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
            }
        } catch {}

        return headers;
    }

    private buildUrl(endpoint: string, params?: Record<string, any>): string {
        const baseUrl = BASIC_CONSTANTS_NETWORK.BASE_URL;
        if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
            const url = new URL(`${baseUrl}${endpoint}`);
            if (params) {
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        url.searchParams.append(key, String(value));
                    }
                });
            }
            return url.toString();
        } else {
            const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
            let url = `${cleanBase}${cleanEndpoint}`;

            if (params) {
                const searchParams = new URLSearchParams();
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        searchParams.append(key, String(value));
                    }
                });
                const queryString = searchParams.toString();
                if (queryString) {
                    url += `?${queryString}`;
                }
            }
            return url;
        }
    }

    private async handleResponse<T>(response: Response, endpoint: string): Promise<T> {
        const responseBody = await response.json().catch(() => ({}));

        if (!response.ok) {
            console.error('[NetworkUtil] Error response:', responseBody);

            const errorCode = typeof responseBody.code === 'number'
                ? responseBody.code
                : typeof responseBody.code === 'string'
                    ? parseInt(responseBody.code, 10) || response.status
                    : response.status;

            throw new NetworkError(
                responseBody.message || 'Unknown error',
                responseBody.timestamp ? new Date(responseBody.timestamp) : new Date(),
                errorCode,
                responseBody.path || endpoint,
                responseBody
            );
        }

        return responseBody as T;
    }

    async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
        const url = this.buildUrl(endpoint, params);
        const headers = this.getAuthHeaders();

        const response = await fetch(url, {
            credentials: 'include',
            headers
        });

        return this.handleResponse<T>(response, endpoint);
    }

    async post<T, B = any>(
        endpoint: string,
        body?: B,
        queryParams?: Record<string, any>,
        extraHeaders?: Record<string, string>
    ): Promise<T> {
        const url = this.buildUrl(endpoint, queryParams);
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            ...this.getAuthHeaders()
        };

        if (extraHeaders) {
            Object.assign(headers, extraHeaders);
        }

        const response = await fetch(url, {
            method: "POST",
            headers,
            body: body ? JSON.stringify(body) : undefined,
            credentials: 'include',
        });

        return this.handleResponse<T>(response, endpoint);
    }

    async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
        const url = this.buildUrl(endpoint);
        const headers = this.getAuthHeaders();
        const response = await fetch(url, {
            method: "POST",
            headers,
            body: formData,
            credentials: 'include'
        });

        return this.handleResponse<T>(response, endpoint);
    }

    async put<T, B = any>(endpoint: string, body?: B): Promise<T> {
        const url = this.buildUrl(endpoint);
        const headers = {
            "Content-Type": "application/json",
            ...this.getAuthHeaders()
        };

        const response = await fetch(url, {
            method: "PUT",
            headers,
            body: body ? JSON.stringify(body) : undefined,
            credentials: 'include',
        });

        return this.handleResponse<T>(response, endpoint);
    }

    async patch<T, B = any>(
        endpoint: string,
        body?: B,
        queryParams?: Record<string, any>,
        extraHeaders?: Record<string, string>
    ): Promise<T> {
        const url = this.buildUrl(endpoint, queryParams);
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            ...this.getAuthHeaders()
        };
        if (extraHeaders) Object.assign(headers, extraHeaders);

        const response = await fetch(url, {
            method: "PATCH",
            headers,
            body: body ? JSON.stringify(body) : undefined,
            credentials: 'include',
        });
        return this.handleResponse<T>(response, endpoint);
    }

    async delete<T>(endpoint: string): Promise<T> {
        const url = this.buildUrl(endpoint);
        const headers = this.getAuthHeaders();

        const response = await fetch(url, {
            method: "DELETE",
            headers,
            credentials: 'include'
        });

        return this.handleResponse<T>(response, endpoint);
    }
}

export { NetworkUtil };